import type {
  PaginatedResponse,
  PaginationMeta,
  PublicChecklistDetailDto,
  PublicChecklistDto,
  PublicChecklistItemDto,
  PublicChecklistUser,
} from './types';

/**
 * The one place the untyped public-checklist responses are read.
 *
 * TODO(MDC-4303): the spec types these endpoints `200: string` / `Array<unknown> | null` (B1), so
 * there is nothing to trust at compile time and the exact field names are still being settled with
 * the backend (B3/B4). Rather than scattering casts through the hooks, every response passes
 * through here: each field is read defensively, a row that cannot be addressed is dropped, and the
 * callers downstream see a plain `PublicChecklistDto`. When the spec is fixed most of this
 * collapses into `Schemas.*` and a straight assignment.
 */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** First candidate that is a non-empty string. */
const readString = (...candidates: unknown[]): string | null => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.length > 0) {
      return candidate;
    }
  }

  return null;
};

/** First candidate that is a finite number, or a string holding one — Laravel counts arrive as both. */
const readNumber = (...candidates: unknown[]): number | null => {
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate;
    }

    if (typeof candidate === 'string' && candidate.trim() !== '' && Number.isFinite(Number(candidate))) {
      return Number(candidate);
    }
  }

  return null;
};

/** First candidate that reads as a boolean. PHP serialises these as `1`/`0` about as often as `true`/`false`. */
const readBoolean = (...candidates: unknown[]): boolean | null => {
  for (const candidate of candidates) {
    if (typeof candidate === 'boolean') {
      return candidate;
    }

    if (candidate === 1 || candidate === '1' || candidate === 'true') {
      return true;
    }

    if (candidate === 0 || candidate === '0' || candidate === 'false') {
      return false;
    }
  }

  return null;
};

/**
 * First candidate usable as an identifier, as a string.
 *
 * Numbers count: the detail endpoint keys a checklist's items by their numeric `id` and sends no
 * uuid for them at all, so an item's identity has to survive being a number.
 */
const readIdentifier = (...candidates: unknown[]): string | null => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.length > 0) {
      return candidate;
    }

    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return String(candidate);
    }
  }

  return null;
};

const readArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** First candidate that is a plain object — the envelope names its paginator in more than one way. */
const firstRecord = (...candidates: unknown[]): Record<string, unknown> | null => {
  for (const candidate of candidates) {
    if (isRecord(candidate)) {
      return candidate;
    }
  }

  return null;
};

/** First candidate that is an array — the rows live one, two or no wrappers deep. */
const firstArray = (...candidates: unknown[]): unknown[] => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

/**
 * The checklist's owner.
 *
 * Read off the row itself as `user_name` / `user_surname`, which is how the API sends it, falling
 * back to a nested `user` object. Note there is no owner id in either form yet — which is why
 * `belongs_to_current_user` matters (see below) and why this can hand back a user with a null `id`.
 */
const normalizeUser = (raw: Record<string, unknown>): PublicChecklistUser | null => {
  const nested = isRecord(raw.user) ? raw.user : null;

  const id = readNumber(nested?.id, raw.user_id);
  const name = readString(nested?.name, raw.user_name);
  const surname = readString(nested?.surname, raw.user_surname);

  if (id === null && name === null && surname === null) {
    return null;
  }

  return { id, name, surname };
};

export const normalizePublicChecklistItem = (raw: unknown): PublicChecklistItemDto | null => {
  if (!isRecord(raw)) {
    return null;
  }

  const uuid = readIdentifier(raw.uuid, raw.id);

  if (!uuid) {
    return null;
  }

  return {
    uuid,
    name: readString(raw.name),
    position: readNumber(raw.position),
  };
};

/**
 * One list row.
 *
 * `null` when the row carries no uuid: that is the id the detail endpoint, the copy flow and the
 * card's navigation are all keyed by, so a row without one cannot be opened and is better left out
 * of the list than rendered as a dead tile.
 */
export const normalizePublicChecklist = (raw: unknown): PublicChecklistDto | null => {
  if (!isRecord(raw)) {
    return null;
  }

  const category = isRecord(raw.category) ? raw.category : null;
  const image = isRecord(raw.image) ? raw.image : null;

  // `id` is read as a fallback for both ids and settles into whichever it fits: a uuid is a string
  // and never parses as a number, a Laravel id is a number and is not a uuid.
  const uuid = readString(raw.uuid, typeof raw.id === 'string' ? raw.id : null);

  if (!uuid) {
    return null;
  }

  return {
    uuid,
    laravel_id: readNumber(raw.laravel_id, raw.id),
    title: readString(raw.title),
    description: readString(raw.description),
    /*
     * `image_uuid` is the only name the API uses, and `readString` rather than `readIdentifier` is
     * deliberate: a number here would be a `files.laravel_id`, which belongs to the old file
     * service and not to the public route this uuid is destined for. Accepting one is what put a
     * numeric id behind a uuid-shaped field and left every public cover broken, so a number is now
     * no cover at all — a placeholder instead of a dead image.
     */
    image_uuid: readString(raw.image_uuid, image?.uuid, image?.id),
    category_uuid: readString(raw.category_uuid, category?.uuid, typeof category?.id === 'string' ? category.id : null),
    category_name: readString(raw.category_name, category?.name),
    user: normalizeUser(raw),
    items_count: readNumber(raw.items_count) ?? readArray(raw.items).length,
    files_count: readNumber(raw.files_count) ?? readArray(raw.files).length,
    clones_count: readNumber(raw.clones_count) ?? 0,
    comments_count: readNumber(raw.comments_count) ?? 0,
    user_followers_count: readNumber(raw.user_followers_count, raw.followers_count) ?? 0,
    is_followed_by_current_user: readBoolean(raw.is_followed_by_current_user, raw.is_followed) ?? false,
    // Left `null` rather than defaulted to `false`: the caller falls back to comparing user ids, and
    // a hard `false` here would claim the server answered when it didn't.
    belongs_to_current_user: readBoolean(raw.belongs_to_current_user, raw.is_owner),
    published_at: readString(raw.published_at),
    created_at: readString(raw.created_at),
    updated_at: readString(raw.updated_at),
  };
};

/**
 * The checklist and its items, out of whatever the detail endpoint wrapped them in.
 *
 * The API answers `{ checklist, items }` — the items are a **sibling** of the row, not a field on
 * it — while the spec annotates the endpoint as a bare array (B1) and Laravel would wrap a resource
 * in `data`. All three are peeled here, one layer at a time, so the reader below only ever sees a
 * row and a list.
 */
const unwrapDetail = (raw: unknown): { row: unknown; items: unknown[] } => {
  const withoutArray = Array.isArray(raw) ? raw.at(0) : raw;
  const withoutData = isRecord(withoutArray) && 'data' in withoutArray ? withoutArray.data : withoutArray;
  const envelope = Array.isArray(withoutData) ? withoutData.at(0) : withoutData;

  if (!isRecord(envelope)) {
    return { row: envelope, items: [] };
  }

  const row = isRecord(envelope.checklist) ? envelope.checklist : envelope;

  // Beside the row, or on it — whichever this response used.
  return { row, items: firstArray(envelope.items, isRecord(row) ? row.items : undefined) };
};

/** The detail row and its items — one request draws the whole page. */
export const normalizePublicChecklistDetail = (raw: unknown): PublicChecklistDetailDto | null => {
  const { row, items } = unwrapDetail(raw);
  const checklist = normalizePublicChecklist(row);

  if (!checklist) {
    return null;
  }

  const normalizedItems = items
    .map(normalizePublicChecklistItem)
    .filter((item): item is PublicChecklistItemDto => item !== null)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  return {
    ...checklist,
    // The row carries its own `items_count`; the sibling list stands in when it doesn't, and both
    // read zero for a checklist that genuinely has no items.
    items_count: checklist.items_count || normalizedItems.length,
    items: normalizedItems,
  };
};

/**
 * The paginator, whatever it was called on the wire.
 *
 * Both spellings of every field are read: the API answers `paginatorInfo` in Lumen's camelCase
 * (`currentPage`, `perPage`, `lastItem`), and Laravel's own paginator writes the same numbers as
 * snake_case. Missing numbers are derived from the rows in hand, so a response with no paginator at
 * all still reads as one full page rather than as nothing.
 */
const normalizeMeta = (
  source: Record<string, unknown> | null,
  rowCount: number,
  requestedPerPage?: number,
): PaginationMeta => {
  const total = readNumber(source?.total) ?? rowCount;
  const perPage =
    readNumber(source?.per_page, source?.perPage, source?.pageSize) ?? requestedPerPage ?? (rowCount || 1);
  const currentPage = readNumber(source?.current_page, source?.currentPage) ?? 1;
  const lastPage =
    readNumber(source?.last_page, source?.lastPage) ?? Math.max(1, Math.ceil(total / Math.max(perPage, 1)));

  // `from`/`to` are what the dashboard prints as "Viewing 1 - 10 of 240". Derived when the envelope
  // doesn't carry them, and null on an empty page — "Viewing 1 - 0" reads as a bug.
  const from = readNumber(source?.from, source?.firstItem) ?? (rowCount > 0 ? (currentPage - 1) * perPage + 1 : null);
  const to = readNumber(source?.to, source?.lastItem) ?? (rowCount > 0 && from !== null ? from + rowCount - 1 : null);

  return {
    currentPage,
    lastPage,
    perPage,
    total,
    from,
    to,
    hasMorePages: readBoolean(source?.has_more_pages, source?.hasMorePages) ?? currentPage < lastPage,
  };
};

/**
 * A page of public checklists.
 *
 * The endpoint answers `{ data, paginatorInfo }` — the same envelope the GraphQL list it replaces
 * used, carried over into REST. The other shapes the backend could still settle on (B4) are read
 * too: a resource collection's `{ data, meta }`, a plain paginator's flat `{ data, current_page, … }`
 * and a bare array. Anything unreadable degrades to a single empty page rather than throwing: the
 * list renders its empty state, which beats a crashed screen.
 */
export const normalizePaginatedPublicChecklists = (
  raw: unknown,
  requestedPerPage?: number,
): PaginatedResponse<PublicChecklistDto> => {
  const envelope = isRecord(raw) ? raw : null;
  const inner = isRecord(envelope?.data) ? envelope.data : null;

  const rows = firstArray(raw, envelope?.data, inner?.data);
  const data = rows.map(normalizePublicChecklist).filter((row): row is PublicChecklistDto => row !== null);

  // `paginatorInfo` first, then the alternatives, and the envelope itself last — a flat paginator
  // keeps its counts beside `data`.
  const metaSource = firstRecord(
    envelope?.paginatorInfo,
    envelope?.meta,
    inner?.paginatorInfo,
    inner?.meta,
    inner,
    envelope,
  );

  return { data, meta: normalizeMeta(metaSource, data.length, requestedPerPage) };
};
